import React, { useState, useMemo, useEffect } from 'react';
import { useUserData } from '../../hooks/useUserData';
import UserCard from '../common/UserCard';
import ClientFilesView from './ClientFilesView';
import FilterBar from '../common/FilterBar';
import { PageTitle, ViewToggle, ToggleButton } from '../common/common.styles';
import { ClientGrid } from './DataEntryDashboard.styles';

const AdminDashboard = () => {
  const { users, documents, loading } = useUserData();
  
  // State for all controls - identical to DataEntryDashboard
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    setStatusFilter(viewMode === 'Active' ? 'Pending' : 'All');
  }, [viewMode]);

  // Filtering logic - identical to DataEntryDashboard
  const filteredClients = useMemo(() => {
    const clients = users.filter(user => user.role === 'client');
    const docsByUser = documents.reduce((acc, doc) => {
        (acc[doc.userId] = acc[doc.userId] || []).push(doc);
        return acc;
    }, {});
    return clients.filter(client => {
        const clientDocs = docsByUser[client.id] || [];
        if (clientDocs.length === 0) return false;
        const hasMatchingDocs = clientDocs.some(doc => {
            const docDate = doc.uploadedAt.toDate();
            const matchesStatus = statusFilter === 'All' || doc.status === statusFilter;
            const matchesStartDate = !startDate || docDate >= startDate;
            const matchesEndDate = !endDate || docDate < new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
            return matchesStatus && matchesStartDate && matchesEndDate;
        });
        if (!hasMatchingDocs) return false;
        const hasPending = client.pendingCount > 0;
        if (viewMode === 'Active' && !hasPending) return false;
        if (viewMode === 'Archived' && hasPending) return false;
        return client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) || client.email.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [users, documents, searchTerm, viewMode, statusFilter, startDate, endDate]);

  if (loading) return <PageTitle>Loading Data...</PageTitle>;

  if (selectedUser) {
    return <ClientFilesView client={selectedUser} documents={documents.filter(d => d.userId === selectedUser.id)} onBack={() => setSelectedUser(null)} />;
  }
    
  return (
    <div>
      <PageTitle>Client Management</PageTitle>
      <ViewToggle>
        <ToggleButton active={viewMode === 'Active'} onClick={() => setViewMode('Active')}>Active</ToggleButton>
        <ToggleButton active={viewMode === 'Archived'} onClick={() => setViewMode('Archived')}>Archived</ToggleButton>
      </ViewToggle>
      
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      
      <ClientGrid>
        {filteredClients.map(client => (
          <UserCard key={client.id} user={client} onClick={() => setSelectedUser(client)} />
        ))}
      </ClientGrid>
    </div>
  );
};

export default AdminDashboard;